<template>
  <div v-if="multiSitePayload.length > 0 && renderComponent">
    <div class="columns">
      <div class="column" :class="$style.headerTitleColumn">
        <label
          :class="[$style.tableName, $style.criteriaBasedLabel]"
          class="label"
          >CRITERIA BASED SAMPLING (OPTIONAL)</label
        >
      </div>
    </div>
    <b-table :class="$style.criteriaBasedTable" :data="auditCriteriaList">
      <template slot-scope="props">
        <b-table-column field="index" label="No." width="10px">
          {{ props.index + 1 }}.
        </b-table-column>
        <b-table-column
          field="criteria"
          label="Data Collection Period Criteria"
        >
          {{ props.row.criteriaTitle }}
        </b-table-column>
        <b-table-column
          width="140px"
          field="sampleSize"
          label="Sample Size"
          :header-class="$style.calculatorHeader"
        >
          <template slot="header">
            Sample Size
            <template v-if="editable">
              <ConsecutiveCalculator
                v-if="renderComponent"
                :initialData="criteriaBasedCalculateData"
                :siteArray="siteArray"
                @applySampleSize="handleApplySampleSizeForCriteriaBased"
              ></ConsecutiveCalculator>
            </template>
          </template>
          <bField horizontal class="sampleSizeInputContainer">
            <b-input
              :disabled="!editable"
              v-model="props.row.sampleSize"
              type="number"
              onkeydown="javascript: return event.keyCode === 8 || event.keyCode === 46 ? true : !isNaN(Number(event.key))"
              :class="$style.numberInput"
              @change.native="updateChildSamplingSize(props.index)"
              min="0"
              oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
              maxlength="7"
            ></b-input>
          </bField>
          <template v-for="(site, siteIndex) in siteArray">
            <bField
              horizontal
              class="sampleSizeInputContainer"
              :label="site.name"
            >
              <b-input
                :disabled="!editable"
                v-model="props.row.sites[siteIndex].sampleSize"
                type="number"
                onkeydown="javascript: return event.keyCode === 8 || event.keyCode === 46 ? true : !isNaN(Number(event.key))"
                :class="$style.numberInput"
                @change.native="
                  updateOtherChildSamplingSize(props.index, siteIndex)
                "
                min="0"
                oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);"
                maxlength="7"
              ></b-input>
            </bField>
          </template>
        </b-table-column>
      </template>
    </b-table>
    <div :class="$style.saveChangesContainer" v-if="editable">
      <b-button
        @click="handleMultiSiteChanges"
        :class="$style.saveChangesBtn"
        type="is-primary"
        >Save</b-button
      >
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { Action, State } from 'vuex-class';
import HeroBar from '@/components/base/HeroBar.vue';
import { isDifferent, isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import {
  AuditSamplingSite,
  MultiSitePayload,
  MultiSiteSamplingRequestPayload,
  AuditSite,
  AuditSamplingDataConfig,
  AuditSamplingResponse,
  AuditCriteriaMapStatus,
} from '@/store/modules/audits/types/audits.types';
import {
  map as _map,
  round as _round,
  cloneDeep as _cloneDeep,
  orderBy as _orderBy,
  findIndex as _findIndex,
  find as _find,
} from 'lodash';
import { ToastProgrammatic as Toast } from 'buefy';
import SamplingCalculator from '@/views/Audit/components/SamplingCalculator.vue';
import ConsecutiveCalculator from '@/views/Audit/components/ConsecutiveCalculator.vue';
import {
  AuditSamplingType,
  ConsecutiveSamplingMode,
} from '@/jbi-shared/types/project.types';

@Component({
  components: {
    ConsecutiveCalculator,
    SamplingCalculator,
    DefaultLayout,
    HeroBar,
  },
})
export default class ConsecutiveTargetSampleMultiSiteSampling extends Vue {
  @Prop()
  public auditId!: number;
  @Prop()
  public auditSiteList!: AuditSite[];
  @Prop()
  public auditConfig!: AuditSamplingResponse;
  @Prop()
  public auditSamplingType!: AuditSamplingType;
  @Prop()
  public consecutiveSamplingMode!: ConsecutiveSamplingMode;
  @Prop({ default: true })
  public editable!: boolean;
  public auditStartDate: any = null;
  public multiSitePayload: MultiSitePayload[] = [];
  public siteArray: AuditSamplingSite[] = [];
  public renderComponent: boolean = true;
  public calculateData = {
    output: 0,
  };

  public criteriaBasedCalculateData = {
    ...this.calculateData,
    sites: this.siteArray,
  };

  @Action('audits/updateMultiSiteSampling')
  public updateMultiSiteSampling!: (
    payload: MultiSiteSamplingRequestPayload,
  ) => void;

  @State((state) => state.audits.updatedMultiSiteSampling)
  public updatedMultiSiteSampling!: any;

  @Watch('updatedMultiSiteSampling')
  @isTruthy
  public watchUpdatedMultiSiteSampling() {
    this.showNotificationAndRedirectToProject();
  }

  public mounted() {
    this.populateMultiSiteData();
  }

  public populateMultiSiteData() {
    this.auditSiteList.forEach((auditSite: AuditSite) => {
      const siteObject: AuditSamplingSite = {
        siteId: auditSite.site.id,
        name: auditSite.site.name,
        sampleSize: 0,
        auditSiteId: auditSite.id,
        calculateData: _cloneDeep({
          ...this.calculateData,
          auditSiteId: auditSite.id,
        }),
      };
      this.siteArray.push(siteObject);
    });

    this.auditConfig.dataConfigs.forEach(
      (dataConfig: AuditSamplingDataConfig) => {
        const currentCriteriaPayload: MultiSitePayload = {
          auditId: this.auditId,
          criteriaId: 0, // todo: change this logic later
          auditCriteriaId: 0,
          sites: [],
          sampleSize: 0,
        };
        Object.values(dataConfig).forEach(
          (singleConfig: AuditSamplingDataConfig) => {
            if (
              singleConfig.auditCriteriaMap.status ===
              AuditCriteriaMapStatus.ACTIVE
            ) {
              currentCriteriaPayload.criteriaId =
                singleConfig.auditCriteriaMap.criteria.id;
              currentCriteriaPayload.auditId = this.auditId;
              currentCriteriaPayload.auditCriteriaId =
                singleConfig.auditCriteriaMap.id;
              currentCriteriaPayload.criteriaTitle =
                singleConfig.auditCriteriaMap.criteria.title;
              currentCriteriaPayload.order =
                singleConfig.auditCriteriaMap.order;
              if (singleConfig.auditSiteMap === null) {
                currentCriteriaPayload.sampleSize = _round(
                  singleConfig.sampleSize,
                );
                currentCriteriaPayload.dataConfig = singleConfig;
              } else {
                const auditSamplingConfig = _find(
                  this.siteArray,
                  (value: AuditSamplingSite) => {
                    return value.auditSiteId === singleConfig.auditSiteMap.id;
                  },
                );
                if (auditSamplingConfig) {
                  currentCriteriaPayload.sites.push({
                    ...auditSamplingConfig,
                    sampleSize: _round(singleConfig.sampleSize),
                    dataConfig: singleConfig,
                  });
                }
              }
            }
          },
        );
        this.multiSitePayload.push(currentCriteriaPayload);
      },
    );
    this.multiSitePayload = this.multiSitePayload.map(
      (criteria: MultiSitePayload) => {
        const sitesIdsToCheckAgainst: number[] = this.siteArray.map(
          (site: AuditSamplingSite) => site.siteId,
        );
        return {
          ...criteria,
          sites: criteria.sites.sort(
            (optionA: AuditSamplingSite, optionB: AuditSamplingSite) => {
              if (
                sitesIdsToCheckAgainst.indexOf(optionA.siteId) >
                sitesIdsToCheckAgainst.indexOf(optionB.siteId)
              ) {
                return 1;
              } else {
                return -1;
              }
            },
          ),
        };
      },
    );
  }

  public showNotificationAndRedirectToProject() {
    Toast.open({
      message: `Changes saved`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
  }

  public updateChildSamplingSize(index: number) {
    const currentSamplingData = this.auditCriteriaList[index];
    const parentVal = !currentSamplingData.sampleSize
      ? 0
      : currentSamplingData.sampleSize;
    const siteArrayLength = this.siteArray.length;
    const modulo = parentVal % siteArrayLength;
    const childVal = (parentVal - modulo) / siteArrayLength;
    this.auditCriteriaList[index].sites.forEach(
      (currentVal: AuditSamplingSite, siteIndex: number) => {
        this.auditCriteriaList[index].sites[siteIndex].sampleSize =
          siteIndex === 0 ? childVal + modulo : childVal;
      },
    );
  }

  public updateOtherChildSamplingSize(index: number, siteIndex: number) {
    const currentSamplingData = this.auditCriteriaList[index];
    const currentElementVal = !this.auditCriteriaList[index].sites[siteIndex]
      .sampleSize
      ? 0
      : Number(this.auditCriteriaList[index].sites[siteIndex].sampleSize);
    const parentVal: number = !currentSamplingData.sampleSize
      ? 0
      : Number(currentSamplingData.sampleSize);

    if (parentVal < currentElementVal) {
      this.updateChildSamplingSize(index);
    } else {
      const tempParentVal = parentVal - currentElementVal;
      const siteArrayLength = Number(this.siteArray.length - 1);
      const modulo = tempParentVal % siteArrayLength;
      const childVal = (tempParentVal - modulo) / siteArrayLength;
      this.auditCriteriaList[index].sites.forEach(
        (currentVal: AuditSamplingSite, currentSiteIndex: number) => {
          if (currentSiteIndex !== siteIndex && siteIndex !== 0) {
            this.auditCriteriaList[index].sites[currentSiteIndex].sampleSize =
              currentSiteIndex === 0 ? childVal + modulo : childVal;
          } else if (currentSiteIndex !== siteIndex) {
            this.auditCriteriaList[index].sites[currentSiteIndex].sampleSize =
              currentSiteIndex === 1 ? childVal + modulo : childVal;
          }
        },
      );
    }
  }

  public updateTotalSamplingSize(index: number) {
    let total = 0;
    this.auditCriteriaList[index].sites.forEach(
      (currentVal: AuditSamplingSite, currentSiteIndex: number) => {
        total =
          Number(total) +
          Number(
            this.auditCriteriaList[index].sites[currentSiteIndex].sampleSize,
          );
      },
    );
    this.auditCriteriaList[index].sampleSize = total;
  }

  public handleMultiSiteChanges() {
    this.updateMultiSiteSampling({
      auditId: this.auditId,
      multiSitePayload: this.multiSitePayload,
      auditSamplingType: this.auditSamplingType,
      auditSamplingMode: this.consecutiveSamplingMode,
      calculateData: this.criteriaBasedCalculateData,
      samplingConfigId: this.auditConfig.auditSamplingConfigs.id,
    });
  }

  public handleApplySampleSizeForCriteriaBased(newValue: any) {
    this.criteriaBasedCalculateData = newValue;
    this.auditCriteriaList.forEach(
      (currentObject: MultiSitePayload, currentIndex: number) => {
        currentObject.sampleSize = newValue.output;
        currentObject.sites.forEach(
          (currentSiteData: AuditSamplingSite, currentSiteIndex: number) => {
            newValue.sites.forEach((siteData: any, siteIndex: number) => {
              if (currentSiteData.auditSiteId === siteData.auditSiteId) {
                currentSiteData.sampleSize = siteData.calculateData.output;
              }
            });
          },
        );
      },
    );
  }

  get auditCriteriaList() {
    return _orderBy(this.multiSitePayload, ['order'], ['asc']);
  }
}
</script>
<style lang="scss" module>
.tableName {
  color: #444f51;
  font-weight: bold;
}
.saveChangesBtn {
  float: right;
  border-radius: 24px;
  height: 48px;
  width: 120px;
  padding: 0 calc(1em + 0.25em);
}
.paddingTop {
  padding-top: 30px;
}
.criteriaBasedLabel {
  margin-top: 12px;
  padding-top: 24px;
}
.saveChangesContainer {
  padding-top: 60px;
  text-align: right;
}
.numberInput {
  width: 80px !important;
  height: 44px;
  input {
    background-color: white !important;
    border-color: #dfdfdf !important;
    color: #444f51 !important;
    text-align: right;
    height: 44px;
    padding: 7px 7px;
  }
}
input[type='number'] {
  -moz-appearance: textfield;
}
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.calculatorHeader {
  float: right;
}
.headerTitleColumn {
  padding: 0 0.75rem;
}
.criteriaBasedTable > div > table {
  thead {
    background-color: #ffffff;
    tr {
      font-size: 14px;
      th {
        color: #9f9f9f;
        font-weight: initial !important;
        padding: 20px 0px 23px 12px;
      }
      th:first-child {
        padding-left: 20px;
      }
      th:last-child {
        padding-left: 0;
        padding-right: 20px;
      }
      th:nth-child(2) {
        padding-left: 42px;
      }
      th:nth-child(3) {
        div {
          float: right;
        }
      }
    }
  }
  tbody {
    background-color: #ffffff;
    tr {
      td {
        color: #444f51;
        border: 0.5px solid #dbdbdb;
        border-width: 0 0 0.5px;
        padding-top: 20px;
        padding-bottom: 18px;
        padding-right: 0px;
      }
      td:first-child {
        padding-left: 20px;
      }
      td:nth-child(2) {
        padding-left: 42px;
      }
      td:last-child {
        padding-left: 0;
        padding-right: 20px;
        padding-top: 20px;
      }
    }
    tr:last-child {
      td {
        padding-bottom: 20px;
      }
    }
  }
}
</style>
